import { useEffect, useRef, useState } from 'react';

import { ChatEmptyState } from './ChatEmptyState';
import { ChatLoadingIndicator } from './ChatLoadingIndicator';
import { MessageBubble } from './MessageBubble';
import type { ChatMessage } from './chatTypes';
import styles from './MessageList.module.css';

interface MessageListProps {
  chapterImageUrls?: ReadonlySet<string>;
  isLoading: boolean;
  messages: ChatMessage[];
  onRetryMessage: (messageId: string) => void;
}

function isNearBottom(element: HTMLDivElement) {
  return element.scrollHeight - element.scrollTop - element.clientHeight < 48;
}

export function MessageList({
  chapterImageUrls,
  isLoading,
  messages,
  onRetryMessage,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [stickToBottom, setStickToBottom] = useState(true);

  useEffect(() => {
    const element = containerRef.current;

    if (!element || !stickToBottom) {
      return;
    }

    element.scrollTop = element.scrollHeight;
  }, [messages, isLoading, stickToBottom]);

  return (
    <div
      className={styles.list}
      onScroll={() => {
        const element = containerRef.current;

        if (!element) {
          return;
        }

        setStickToBottom(isNearBottom(element));
      }}
      ref={containerRef}
    >
      {messages.length === 0 && !isLoading ? <ChatEmptyState /> : null}

      {messages.map((message) => (
        <MessageBubble
          chapterImageUrls={chapterImageUrls}
          key={message.id}
          message={message}
          onRetry={onRetryMessage}
        />
      ))}

      {isLoading ? <ChatLoadingIndicator /> : null}
    </div>
  );
}
